import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCreditCard, FaCalendarAlt, FaClock, FaHome, FaDoorOpen,
  FaDownload, FaFilter, FaSearch, FaSpinner, FaCheckCircle,
  FaTimesCircle, FaHourglassHalf, FaUndo, FaReceipt, FaRupeeSign,
} from 'react-icons/fa';
import { fetchMyPayments, fetchPaymentInvoice, generateInvoicePDF, getPaymentStatusStyle } from '../services/paymentService';
import Navbar from '../components/Navbar';
import './UserPaymentHistory.css';

const UserPaymentHistory = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'stay' | 'meeting'
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    fetchMyPayments(token)
      .then(setPayments)
      .catch((err) => toast.error(err.message || 'Failed to load payment history'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const stats = useMemo(() => {
    const paid = payments.filter((p) => p.status === 'paid');
    return {
      total: payments.length,
      totalPaid: paid.length,
      totalAmount: paid.reduce((s, p) => s + (p.totalAmount || 0), 0),
      stayAmount: paid.filter((p) => p.bookingType === 'stay').reduce((s, p) => s + (p.totalAmount || 0), 0),
      meetingAmount: paid.filter((p) => p.bookingType === 'meeting').reduce((s, p) => s + (p.totalAmount || 0), 0),
      refunds: payments.filter((p) => p.status === 'refunded').length,
    };
  }, [payments]);

  const filtered = useMemo(() => {
    let list = payments;
    if (typeFilter !== 'all') list = list.filter((p) => p.bookingType === typeFilter);
    if (statusFilter !== 'all') list = list.filter((p) => p.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          (p.propertyName || '').toLowerCase().includes(q) ||
          (p.roomName || '').toLowerCase().includes(q) ||
          (p.razorpayPaymentId || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [payments, typeFilter, statusFilter, search]);

  const handleDownloadInvoice = async (payment) => {
    if (payment.status !== 'paid') {
      toast.info('Invoice is only available for paid transactions.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setDownloading(payment._id);
    try {
      const invoice = await fetchPaymentInvoice(token, payment._id);
      await generateInvoicePDF(invoice);
      toast.success('Invoice downloaded!');
    } catch (err) {
      toast.error(err.message || 'Failed to download invoice');
    } finally {
      setDownloading(null);
    }
  };

  const StatusIcon = ({ status }) => {
    const icons = {
      paid: <FaCheckCircle style={{ color: '#10b981' }} />,
      pending: <FaHourglassHalf style={{ color: '#f59e0b' }} />,
      failed: <FaTimesCircle style={{ color: '#ef4444' }} />,
      refunded: <FaUndo style={{ color: '#3b82f6' }} />,
    };
    return icons[status] || null;
  };

  const StatusBadge = ({ status }) => {
    const s = getPaymentStatusStyle(status);
    return (
      <span
        className="uph-status-badge"
        style={{ background: s.bg, color: s.textColor, border: `1px solid ${s.color}22` }}
      >
        <StatusIcon status={status} />
        {s.label}
      </span>
    );
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const formatTime = (d) =>
    d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="uph-loading">
          <FaSpinner className="uph-spinner" />
          <p>Loading your payment history...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="uph-page">
        {/* Stats Row */}
        <div className="uph-stats-row">
          <div className="uph-stat-card blue">
            <FaRupeeSign className="uph-stat-icon" />
            <div>
              <div className="uph-stat-label">Total Spent</div>
              <div className="uph-stat-value">₹{stats.totalAmount.toLocaleString()}</div>
            </div>
          </div>
          <div className="uph-stat-card green">
            <FaHome className="uph-stat-icon" />
            <div>
              <div className="uph-stat-label">Stay Payments</div>
              <div className="uph-stat-value">₹{stats.stayAmount.toLocaleString()}</div>
            </div>
          </div>
          <div className="uph-stat-card purple">
            <FaDoorOpen className="uph-stat-icon" />
            <div>
              <div className="uph-stat-label">Meeting Payments</div>
              <div className="uph-stat-value">₹{stats.meetingAmount.toLocaleString()}</div>
            </div>
          </div>
          <div className="uph-stat-card amber">
            <FaUndo className="uph-stat-icon" />
            <div>
              <div className="uph-stat-label">Refunds</div>
              <div className="uph-stat-value">{stats.refunds}</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="uph-filters">
          <div className="uph-search">
            <FaSearch className="uph-search-icon" />
            <input
              type="text"
              placeholder="Search by property, room, or payment ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="uph-filter-group">
            <FaFilter className="uph-filter-icon" />
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              <option value="stay">Stay</option>
              <option value="meeting">Meeting Room</option>
            </select>
          </div>

          <div className="uph-filter-group">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>

        {/* Payment list */}
        {filtered.length === 0 ? (
          <div className="uph-empty">
            <FaReceipt className="uph-empty-icon" />
            <h3>No payments found</h3>
            <p>
              {payments.length === 0
                ? "You haven't made any online payments yet."
                : 'No payments match your current filters.'}
            </p>
          </div>
        ) : (
          <div className="uph-list">
            {filtered.map((p) => (
              <div key={p._id} className="uph-item">
                {/* Type icon + badge */}
                <div className={`uph-type-icon ${p.bookingType}`}>
                  {p.bookingType === 'meeting' ? <FaDoorOpen /> : <FaHome />}
                </div>

                {/* Booking details */}
                <div className="uph-item-details">
                  <div className="uph-item-title">
                    {p.bookingType === 'meeting'
                      ? p.roomName || 'Meeting Room'
                      : p.propertyName || 'Stay Booking'}
                    <span className="uph-type-badge">{p.bookingType === 'meeting' ? 'Meeting' : 'Stay'}</span>
                  </div>

                  <div className="uph-item-meta">
                    {p.bookingType === 'meeting' ? (
                      <>
                        <span>
                          <FaCalendarAlt />
                          {formatDate(p.startTime)}
                        </span>
                        <span>
                          <FaClock />
                          {formatTime(p.startTime)} – {formatTime(p.endTime)}
                        </span>
                        {p.roomCity && <span>📍 {[p.roomLocation, p.roomCity].filter(Boolean).join(', ')}</span>}
                        {p.purpose && <span>🎯 {p.purpose}</span>}
                      </>
                    ) : (
                      <>
                        <span>
                          <FaCalendarAlt />
                          {formatDate(p.checkIn)} – {formatDate(p.checkOut)}
                        </span>
                        {p.propertyCity && <span>📍 {p.propertyCity}</span>}
                      </>
                    )}
                    <span className="uph-paid-date">
                      {p.paidAt ? `Paid on ${formatDate(p.paidAt)}` : `Created ${formatDate(p.createdAt)}`}
                    </span>
                  </div>

                  {p.razorpayPaymentId && (
                    <div className="uph-payment-id">
                      Payment ID: <code>{p.razorpayPaymentId}</code>
                    </div>
                  )}
                </div>

                {/* Amount + status + actions */}
                <div className="uph-item-right">
                  <div className="uph-item-amount">₹{(p.totalAmount || 0).toLocaleString()}</div>
                  <StatusBadge status={p.status} />

                  {p.status === 'paid' && (
                    <button
                      className="uph-invoice-btn"
                      onClick={() => handleDownloadInvoice(p)}
                      disabled={downloading === p._id}
                      title="Download Invoice"
                    >
                      {downloading === p._id ? (
                        <FaSpinner className="uph-spinner-sm" />
                      ) : (
                        <FaDownload />
                      )}
                      Invoice
                    </button>
                  )}

                  {p.status === 'refunded' && p.refundedAt && (
                    <div className="uph-refund-date">Refunded {formatDate(p.refundedAt)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default UserPaymentHistory;
